import { ArraySet } from "./ArraySet";
import { HistoryBuffer } from "./HistoryBuffer";
import { maybeCaptureParent } from "./capture";
import { EMPTY_ARRAY, equals } from "./helpers";
import { advanceGlobalEpoch, atomDidChange, globalEpoch } from "./transactions";
import { RESET_VALUE } from "./types";
import { logDotValueWarning } from "./warnings";
/**
 * WARNING!
 *
 * I made a change to the initialization so that lastChangedEpoch
 * gets set in the constructor rather than in the class itself.
 *
 * This was required to fix some of the tests and _should_ have the
 * equivalent behavior.
 *
 */ /**
 * @internal
 */ export class _Atom {
    name;
    current;
    constructor(name, current, options){
        this.name = name;
        this.current = current;
        this.children = new ArraySet();
        this.isEqual = options?.isEqual ?? null;
        this.lastChangedEpoch = globalEpoch;
        if (!options) return;
        if (options.historyLength) {
            this.historyBuffer = new HistoryBuffer(options.historyLength);
        }
        this.computeDiff = options.computeDiff;
    }
    isEqual;
    computeDiff;
    lastChangedEpoch;
    children;
    historyBuffer;
    __unsafe__getWithoutCapture() {
        return this.current;
    }
    get() {
        maybeCaptureParent(this);
        return this.current;
    }
    /**
   * @deprecated Use [[Atom.get]] instead.
   */ // eslint-disable-next-line no-restricted-syntax
    get value() {
        logDotValueWarning();
        return this.get();
    }
    set(value, diff) {
        // If the value has not changed, do nothing.
        if (this.isEqual?.(this.current, value) ?? equals(this.current, value)) {
            return this.current;
        }
        // Tick forward the global epoch
        advanceGlobalEpoch();
        // Add the diff to the history buffer.
        if (this.historyBuffer) {
            this.historyBuffer.pushEntry(this.lastChangedEpoch, globalEpoch, diff ?? this.computeDiff?.(this.current, value, this.lastChangedEpoch, globalEpoch) ?? RESET_VALUE);
        }
        // Update the atom's record of the epoch when last changed.
        this.lastChangedEpoch = globalEpoch;
        const oldValue = this.current;
        this.current = value;
        // Notify all children that this atom has changed.
        atomDidChange(this, oldValue);
        return value;
    }
    update(updater) {
        return this.set(updater(this.current));
    }
    getDiffSince(epoch) {
        maybeCaptureParent(this);
        // If no changes have occurred since the given epoch, return an empty array.
        if (epoch >= this.lastChangedEpoch) {
            return EMPTY_ARRAY;
        }
        return this.historyBuffer?.getChangesSince(epoch) ?? RESET_VALUE;
    }
}
/**
 * Creates a new [[Atom]].
 *
 * An Atom is a signal that can be updated directly by calling [[Atom.set]] or [[Atom.update]].
 *
 * @example
 * ```ts
 * const name = atom('name', 'John')
 *
 * name.get() // 'John'
 *
 * name.set('Jane')
 *
 * name.get() // 'Jane'
 * ```
 *
 * @public
 */ export function atom(/**
   * A name for the signal. This is used for debugging and profiling purposes, it does not need to be unique.
   */ name, /**
   * The initial value of the signal.
   */ initialValue, /**
   * The options to configure the atom. See [[AtomOptions]].
   */ options) {
    return new _Atom(name, initialValue, options);
}
/**
 * Returns true if the given value is an [[Atom]].
 * @public
 */ export function isAtom(value) {
    return value instanceof _Atom;
}
